/**
 * Lo que el ASESOR decidió sobre un documento, dicho igual en la Ficha y en la
 * tarjeta del documento (DEF-051 y DEF-044, QA de TEST 05-oct-2026).
 *
 * POR QUÉ EXISTE
 * --------------
 * QA pulsó «Pedir de nuevo» sobre el PU. Seis minutos después:
 *
 *   Ficha        «Falta pedirle: PU - Predio Urbano»     (ya se le había pedido)
 *   Documentos   la tarjeta del PU: vigente, en Drive, IA 88, sin ninguna marca
 *   bandeja      el estado del expediente
 *
 * Tres estados distintos del mismo hecho. Y al pulsar «Rechazar» nada le
 * recordaba al asesor que el cliente no se enteraba: el aviso automático
 * espera texto de negocio (`conversation/_revision_del_asesor`).
 *
 * El dato estaba en el requisito del checklist —lo escribe solo la
 * conversación—: `revision` (`teleagent.revision.pendiente_por`), quién, cuándo
 * y sobre qué documento. El backend lo pasa en `progreso.requeridos[]`
 * (`portal_api._revision_del_requisito`) y acá se decide qué se lee, una vez,
 * para la Ficha y para la tarjeta.
 *
 * Puro: lo corre `tests/unit/test_qa_test_06oct_portal.py` en Node sobre lo
 * que devuelve `_progreso_documental` del portal de verdad.
 */
import { fechaHoraEnLima } from './documentos'

/** `teleagent.revision.PENDIENTE_POR`, cruzado por test. */
export const PEDIDO_DE_NUEVO = 'pedido_de_nuevo'
export const RECHAZADO_POR_ASESOR = 'rechazado_por_asesor'

/** Lo que el panel lee de cada requisito. Un subconjunto de `ProgresoDocumental`. */
export interface RequisitoRevisado {
  label: string
  recibido: boolean
  no_aplica?: boolean
  revision?: string
  revisado_por?: string
  revisado_at?: string
  documento_id?: string
}

export interface MarcaDeRevision {
  tipo: typeof PEDIDO_DE_NUEVO | typeof RECHAZADO_POR_ASESOR
  /** «Pedido de nuevo · esperando reenvío» / «Rechazado · el cliente todavía no fue avisado». */
  titulo: string
  /** El chip de la tarjeta: «Pedido de nuevo» / «Sin avisar al cliente». */
  corto: string
  /** «05/10 16:35 · Diego Casallo», o '' si no hay ninguno de los dos. */
  detalle: string
}

/** La marca de un requisito, o `null` si no hay una decisión del asesor pendiente. */
export function marcaDelRequisito(r: RequisitoRevisado | null | undefined): MarcaDeRevision | null {
  if (!r || r.recibido || r.no_aplica) return null
  const detalle = [fechaHoraEnLima(r.revisado_at), (r.revisado_por || '').trim()]
    .filter((x) => x).join(' · ')
  if (r.revision === PEDIDO_DE_NUEVO) {
    return { tipo: PEDIDO_DE_NUEVO, titulo: 'Pedido de nuevo · esperando reenvío',
             corto: 'Pedido de nuevo', detalle }
  }
  if (r.revision === RECHAZADO_POR_ASESOR) {
    return { tipo: RECHAZADO_POR_ASESOR,
             titulo: 'Rechazado · el cliente todavía no fue avisado',
             corto: 'Sin avisar al cliente', detalle }
  }
  return null
}

/**
 * La marca de la tarjeta de un documento: la del requisito que el asesor
 * rechazó o pidió de nuevo SOBRE ESE archivo (`documento_id`). Un rechazado
 * que otra versión sigue cubriendo no tiene marca: el requisito está bien.
 */
export function marcaDelDocumento(docId: string,
                                  requeridos: ReadonlyArray<RequisitoRevisado> | null | undefined,
): MarcaDeRevision | null {
  for (const r of requeridos ?? []) {
    if (!docId || r.documento_id !== docId) continue
    const marca = marcaDelRequisito(r)
    if (marca) return marca
  }
  return null
}

/**
 * ¿La tarjeta ofrece «Pedir de nuevo al cliente» en el lugar de la revisión?
 * Solo con el rechazo que el cliente no conoce (DEF-044): es lo que falta
 * hacer, y desde que se pide la marca pasa a «Pedido de nuevo».
 */
export function ofrecerPedirDeNuevo(marca: MarcaDeRevision | null): boolean {
  return marca?.tipo === RECHAZADO_POR_ASESOR
}

export interface FaltantesDeLaFicha {
  /** «Falta pedirle: …»: lo que no llegó y a nadie se le pidió de nuevo. Un
   *  rechazo sin aviso SÍ va acá: el cliente no sabe que tiene que mandarlo. */
  porPedir: string[]
  /** Lo que ya se le pidió de nuevo y se espera: no es «falta pedirle». */
  esperandoReenvio: string[]
}

/** El resumen del pie de «Documentos del caso». Lo que no aplica no falta. */
export function faltantesDeLaFicha(
  requeridos: ReadonlyArray<RequisitoRevisado> | null | undefined,
): FaltantesDeLaFicha {
  const porPedir: string[] = []
  const esperandoReenvio: string[] = []
  for (const r of requeridos ?? []) {
    if (r.recibido || r.no_aplica) continue
    if (marcaDelRequisito(r)?.tipo === PEDIDO_DE_NUEVO) esperandoReenvio.push(r.label)
    else porPedir.push(r.label)
  }
  return { porPedir, esperandoReenvio }
}
